import { AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatGscUpdatedAtUtc } from "@/lib/date-utils";
import { usePipelineFreshness } from "@/hooks/useClicksData";
import { nextRunLine, type PipelineSource } from "@/lib/pipeline-schedule";

/**
 * How fresh the numbers on this page are, in the same badge language the GSC
 * toolbar uses. Unlike that one, these are server facts: when the sync last
 * ran, how far the data actually reaches, when each matview was last rebuilt.
 * A GSC-style `dataUpdatedAt` badge would only say when this browser last
 * fetched, which is not the question anyone is asking here.
 */
const SOURCE_LABELS: Record<string, string> = {
  keitaro_sync: "Keitaro Sync",
  clicks_data: "Clicks data",
  analytics_data: "Analytics data",
};

const formatTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? "—" : formatGscUpdatedAtUtc(ms);
};

/**
 * `sources`: which badges this page shows - each page only the data it reads.
 * Rows the server sends that are not listed (or not known yet, during a deploy)
 * are skipped rather than shown under a raw job name.
 */
export const PipelineFreshness = ({
  sources = ["keitaro_sync", "clicks_data"],
}: {
  sources?: PipelineSource[];
}) => {
  const { data, isPending, isError } = usePipelineFreshness();

  const rows = data?.filter((row) => (sources as string[]).includes(row.source)) ?? [];
  if (isPending || isError || !rows.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {rows.map((row) => {
        const stale = row.ok === false;
        const unknown = row.ok == null;

        return (
          <Badge
            key={row.source}
            variant="outline"
            className={
              "rounded-full border px-2.5 py-1 font-normal " +
              (stale
                ? "signal-card-warning"
                : unknown
                  ? "border-border"
                  : "signal-card-positive")
            }
            title={[row.detail, nextRunLine(row.source as PipelineSource)].filter(Boolean).join("\n")}
          >
            {stale ? (
              <AlertCircle className="size-3.5 signal-text-warning" />
            ) : unknown ? (
              <Clock className="size-3.5 text-muted-foreground" />
            ) : (
              <CheckCircle2 className="size-3.5 signal-text-positive" />
            )}
            <span
              className={
                stale
                  ? "signal-text-warning"
                  : unknown
                    ? "text-muted-foreground"
                    : "signal-text-positive"
              }
            >
              {SOURCE_LABELS[row.source] ?? row.source}{" "}
              <span className="font-semibold tabular-nums">
                {formatTime(row.last_run_at)}
              </span>
            </span>
          </Badge>
        );
      })}
    </div>
  );
};
